import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { OptionDropdown } from "components/icons/optionDropdown";
import { Padlock } from "components/icons/Padlock";
import { EditJournalEntry } from "components/JournalEntry/EditJournalEntry";
import { JournalEntrySlider } from "components/JournalEntry/JournalEntrySlider";
import { LockedUi } from "components/LockedUi/LockedUi";
import { DISPLAY_DATE } from "constants/date";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import dayjs from "dayjs";
import { useModal } from "hooks/useModal";
import { useShow } from "hooks/useShow";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { Payroll } from "types/Models/payroll";
import DeleteModal from "./DeleteModal";
import PayrollSlider from "./PayrollSlider";

type PayrollTableProps = {
  payrolls: Payroll[];
  entityId: string;
  groupId: string;
};

const SliderWapper = ({
  groupId,
  entityId,
}: {
  groupId: string;
  entityId: string;
}) => {
  const { payrollId } = useParams<{ payrollId: string }>();
  const history = useHistory();
  const { search } = useLocation();

  const onClose = () => {
    history.push(`/books/payroll${search}`);
  };

  return (
    <PayrollSlider
      payrollId={payrollId}
      open={Boolean(payrollId)}
      close={onClose}
      entityId={entityId}
      groupId={groupId}
    />
  );
};

const columnHelper = createColumnHelper<Payroll>();

const Actions = ({
  payroll,
  entityId,
}: {
  payroll: Payroll;
  entityId: string;
}) => {
  const deleteModal = useModal();
  const ledgerEntryId = payroll?.ledger_entry;
  const editModal = useModal();

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <LockedUi
        feature={WRITE_PERMISSION_KEYS.EXPENSE}
        blockedUi={
          <div className="t-pointer-events-none">
            <Button customType="ghost_icon" size="small" disabled>
              <Padlock />
            </Button>
          </div>
        }
      >
        <Dropdown.Root>
          <Dropdown.Trigger asChild>
            <Button customType="ghost_icon" size="small">
              <OptionDropdown />
            </Button>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content className="!t-min-w-40" align="end">
              <Dropdown.Item onSelect={editModal.open}>
                Edit journal entry
              </Dropdown.Item>
              <Dropdown.Item type="danger" onSelect={deleteModal.open}>
                Delete
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      </LockedUi>
      <DeleteModal payroll={payroll} entityId={entityId} {...deleteModal} />
      <EditJournalEntry
        isOpen={editModal.isOpen}
        close={editModal.close}
        ledgerEntryId={ledgerEntryId}
      />
    </div>
  );
};

const LedgerEntry = ({ payroll }: { payroll: Payroll }) => {
  const jeSlider = useShow();
  const ledgerEntryId = payroll?.ledger_entry;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="t-flex t-justify-center"
    >
      <Button onClick={jeSlider.show} customType="link" size="small">
        View
      </Button>
      <JournalEntrySlider
        ledgerEntryId={ledgerEntryId!}
        isOpen={jeSlider.shown}
        onClose={jeSlider.hide}
      />
    </div>
  );
};

const columns = [
  columnHelper.accessor("period", {
    header: () => <div className="t-text-left">PAYROLL PERIOD</div>,
    cell: (info) => (
      <span className="t-text-body-sm t-text-text-60">{info.getValue()}</span>
    ),
    size: 25,
  }),

  columnHelper.accessor("source", {
    header: "SOURCE",
    cell: (info) => (
      <span className="t-text-body-sm t-text-text-60">
        <Avatar alt={info.getValue()} />
      </span>
    ),
    size: 10,
  }),

  columnHelper.accessor("created_at", {
    header: "DATE OF IMPORT",
    cell: (info) => (
      <span className="t-text-body-sm t-text-text-60">
        {dayjs(info.getValue()).format(DISPLAY_DATE)}
      </span>
    ),
    size: 15,
  }),

  columnHelper.accessor("gross_pay", {
    header: () => <div className="t-text-right">GROSS PAY</div>,
    cell: (info) => (
      <div className="t-text-right t-text-body-sm t-text-text-60">
        <AmountSuperScript amount={info.getValue()} />
      </div>
    ),
    size: 20,
  }),

  columnHelper.accessor("ledger_entry", {
    header: () => <div className="t-text-center">LINKED JOURNAL ENTRY</div>,
    cell: (info) => {
      return <LedgerEntry payroll={info.row.original} />;
    },
    size: 25,
  }),

  columnHelper.accessor("uuid", {
    header: "",
    cell: (info) => {
      // @ts-ignore
      const entityId = info.table.options.meta?.entityId;
      return <Actions payroll={info.row.original} entityId={entityId} />;
    },
    size: 5,
  }),
];

export default function PayrollTable({
  payrolls,
  entityId,
  groupId,
}: PayrollTableProps) {
  const { path } = useRouteMatch();
  const history = useHistory();
  const { search } = useLocation();

  const table = useReactTable({
    data: payrolls,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 5,
    },
    meta: {
      entityId,
    },
  });

  const onRowClick = (row: Payroll) => {
    history.push(`/books/payroll/${row.uuid}${search}`);
  };

  return (
    <>
      <TableUI
        table={table}
        onRowClick={(row) => onRowClick(row.original)}
        contentClassName="sm:t-min-w-[700px]"
      />
      <Switch>
        <Route path={`${path}/:payrollId`}>
          <SliderWapper groupId={groupId} entityId={entityId} />
        </Route>
      </Switch>
    </>
  );
}
